// Top-bar alarm bell — open service incidents (down/flap). Badge shows the open
// count; the dropdown lists them; "View all" goes to the /alerts history page.
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api.js';
import { parseTs } from '../../lib/time.js';

function BellIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 20 20" fill="currentColor">
      <path d="M10 2a6 6 0 00-6 6v2.586l-.707.707A1 1 0 004 13h12a1 1 0 00.707-1.707L16 10.586V8a6 6 0 00-6-6zM10 18a2.5 2.5 0 002.45-2h-4.9A2.5 2.5 0 0010 18z" />
    </svg>
  );
}

export function fmtAgo(iso) {
  const at = parseTs(iso);
  if (!at) return '';
  const then = at.getTime();
  const s = Math.max(0, Math.floor((Date.now() - then) / 1000));
  if (s < 90) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 90) return `${m}m ago`;
  const h = Math.floor(m / 60);
  return h < 48 ? `${h}h ago` : `${Math.floor(h / 24)}d ago`;
}

export default function NotificationBell() {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const q = useQuery({
    queryKey: ['service-incidents', 'open'],
    queryFn: () => api.get('/service-incidents', { status: 'open' }),
    refetchInterval: 30_000,
  });
  const incidents = q.data?.incidents || [];
  const count = incidents.length;

  useEffect(() => {
    if (!open) return;
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, [open]);

  const go = (to) => { setOpen(false); navigate(to); };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen((o) => !o)}
        title="Service alerts"
        className={`relative rounded-md p-1.5 transition-colors ${count ? 'text-red-500 hover:bg-red-50' : 'text-gray-400 hover:bg-gray-100 hover:text-gray-700'}`}
      >
        <BellIcon className="h-5 w-5" />
        {count > 0 && (
          <span className="absolute -right-0.5 -top-0.5 inline-flex min-w-[16px] items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-4 text-white">
            {count > 99 ? '99+' : count}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 z-50 mt-2 w-80 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-lg">
          <div className="flex items-center justify-between border-b border-gray-100 px-4 py-2">
            <span className="text-sm font-semibold text-gray-900">Service alerts</span>
            {count > 0 && <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-medium text-red-700">{count} open</span>}
          </div>
          {count === 0 ? (
            <div className="px-4 py-6 text-center text-sm text-gray-400">No open alerts.</div>
          ) : (
            <ul className="max-h-80 divide-y divide-gray-100 overflow-y-auto">
              {incidents.slice(0, 12).map((i) => (
                <li key={i.id}>
                  <button
                    onClick={() => go(i.subscriberId ? `/subscribers/${i.subscriberId}` : '/alerts')}
                    className="flex w-full items-center justify-between gap-2 px-4 py-2 text-left text-sm hover:bg-gray-50"
                  >
                    <span className="flex items-center gap-2">
                      <span className={`inline-flex rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${
                        i.kind === 'down' ? 'bg-red-200 text-red-800' : 'bg-amber-200 text-amber-800'
                      }`}>{i.kind}</span>
                      <span className="font-mono text-gray-800">{i.extension}</span>
                      {i.subscriberName && <span className="truncate text-gray-500">{i.subscriberName}</span>}
                    </span>
                    <span className="flex-shrink-0 text-xs text-gray-400">{fmtAgo(i.openedAt)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <button
            onClick={() => go('/alerts')}
            className="block w-full border-t border-gray-100 px-4 py-2 text-center text-sm font-medium text-blue-600 hover:bg-gray-50"
          >
            View all alerts →
          </button>
        </div>
      )}
    </div>
  );
}
